import React, { useState } from 'react';
import { 
  FiChevronLeft, 
  FiChevronRight, 
  FiChevronsLeft, 
  FiChevronsRight, 
  FiSearch,
  FiFilter,
  FiDownload,
  FiRefreshCw,
  FiGrid,
  FiList,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiAlertCircle,
  FiCheckCircle,
  FiX,
  FiMoreVertical
} from 'react-icons/fi';

const DataTable = ({
  columns,
  data,
  selectable = false,
  onSelect,
  selectedRows = [],
  searchable = false,
  searchPlaceholder = 'Search...',
  pagination = null,
  onPageChange,
  loading = false,
  emptyMessage = 'No data available',
  className = '',
  showActions = true,
  onRefresh,
  onExport,
  viewMode = 'table', // table, card
  onViewModeChange
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAll, setSelectedAll] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const filteredData = searchable && searchTerm
    ? data.filter(row => 
        columns.some(col => {
          const value = row[col.accessor];
          return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
        })
      )
    : data;

  // Sort data
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const handleSelectAll = () => {
    if (selectedAll) {
      onSelect?.([]);
    } else {
      onSelect?.(sortedData.map(row => row.id));
    }
    setSelectedAll(!selectedAll);
  };

  const handleSelectRow = (rowId) => {
    const newSelected = selectedRows.includes(rowId)
      ? selectedRows.filter(id => id !== rowId)
      : [...selectedRows, rowId];
    onSelect?.(newSelected);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const renderCell = (row, column) => {
    if (column.cell) {
      return column.cell(row);
    }
    return row[column.accessor] || '-';
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-8">
        <div className="flex flex-col items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#ff660e] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-400">Loading data...</p>
        </div>
      </div>
    );
  }

  const totalItems = pagination?.total || sortedData.length;
  const startItem = pagination?.from || 1;
  const endItem = pagination?.to || sortedData.length;

  return (
    <div className={`bg-gray-800 rounded-xl border border-gray-700 ${className}`}>
      {/* Table Header with Actions */}
      {(searchable || showActions) && (
        <div className="p-4 border-b border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            {/* Search */}
            {searchable && (
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff660e] text-white placeholder-gray-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            {showActions && (
              <div className="flex items-center space-x-2">
                {onViewModeChange && (
                  <div className="flex bg-gray-700 rounded-lg p-1">
                    <button
                      onClick={() => onViewModeChange('table')}
                      className={`p-2 rounded-md transition-colors ${viewMode === 'table' ? 'bg-[#ff660e] text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                      <FiGrid />
                    </button>
                    <button
                      onClick={() => onViewModeChange('card')}
                      className={`p-2 rounded-md transition-colors ${viewMode === 'card' ? 'bg-[#ff660e] text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                      <FiList />
                    </button>
                  </div>
                )}
                
                {onRefresh && (
                  <button
                    onClick={onRefresh}
                    className="p-2.5 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors text-gray-300 hover:text-white"
                    title="Refresh"
                  >
                    <FiRefreshCw />
                  </button>
                )}
                
                {onExport && (
                  <button
                    onClick={onExport}
                    className="p-2.5 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors text-gray-300 hover:text-white"
                    title="Export"
                  >
                    <FiDownload />
                  </button>
                )}
                
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="p-2.5 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors text-gray-300 hover:text-white"
                  title="Filters"
                >
                  <FiFilter />
                </button>
              </div>
            )}
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-700/50 rounded-lg border border-gray-600">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-300">Filters</h4>
                <button
                  onClick={() => setShowFilters(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <FiX />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Add filter fields here */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Status</label>
                  <select className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm">
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Date Range</label>
                  <select className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm">
                    <option value="">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button className="w-full px-4 py-2 bg-[#ff660e] text-white rounded hover:bg-orange-600 transition-colors text-sm">
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-700">
          <thead className="bg-gray-900">
            <tr>
              {selectable && (
                <th className="w-16 px-4 py-3">
                  <input
                    type="checkbox"
                    className="rounded border-gray-600 bg-gray-700 text-[#ff660e] focus:ring-[#ff660e]"
                    checked={selectedAll}
                    onChange={handleSelectAll}
                  />
                </th>
              )}
              {columns.map((column, index) => (
                <th
                  key={index}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-300 transition-colors"
                  onClick={() => column.sortable && handleSort(column.accessor)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.header}</span>
                    {column.sortable && sortConfig.key === column.accessor && (
                      <span className="text-[#ff660e]">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="px-4 py-12 text-center"
                >
                  <div className="text-gray-600 text-4xl mb-3">
                    {searchTerm ? '🔍' : '📭'}
                  </div>
                  <p className="text-gray-400 text-lg mb-2">{emptyMessage}</p>
                  {searchTerm && (
                    <p className="text-gray-500 text-sm">
                      No results found for "{searchTerm}"
                    </p>
                  )}
                </td>
              </tr>
            ) : (
              sortedData.map((row, rowIndex) => (
                <tr
                  key={row.id || rowIndex}
                  className={`transition-colors ${
                    selectedRows.includes(row.id) 
                      ? 'bg-[#ff660e]/10 hover:bg-[#ff660e]/20' 
                      : 'hover:bg-gray-750'
                  }`}
                >
                  {selectable && (
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        className="rounded border-gray-600 bg-gray-700 text-[#ff660e] focus:ring-[#ff660e]"
                        checked={selectedRows.includes(row.id)}
                        onChange={() => handleSelectRow(row.id)}
                      />
                    </td>
                  )}
                  {columns.map((column, colIndex) => (
                    <td 
                      key={colIndex} 
                      className="px-4 py-3 text-sm"
                    >
                      <div className="text-gray-300">
                        {renderCell(row, column)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer with Pagination and Stats */}
      <div className="px-4 py-3 border-t border-gray-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Selection Stats */}
          {selectable && selectedRows.length > 0 && (
            <div className="flex items-center space-x-2">
              <div className="px-3 py-1 bg-[#ff660e]/20 text-[#ff660e] rounded-full text-sm">
                {selectedRows.length} selected
              </div>
              <button
                onClick={() => onSelect?.([])}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                Clear selection
              </button>
            </div>
          )}

          {/* Pagination Stats */}
          <div className="text-sm text-gray-400">
            Showing <span className="font-medium text-white">{startItem}-{endItem}</span> of{' '}
            <span className="font-medium text-white">{totalItems}</span> items
          </div>

          {/* Pagination Controls */}
          {pagination && pagination.total_pages > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onPageChange?.(1)}
                disabled={pagination.current_page === 1}
                className="p-2 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="First page"
              >
                <FiChevronsLeft className="w-4 h-4 text-gray-300" />
              </button>
              <button
                onClick={() => onPageChange?.(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className="p-2 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Previous page"
              >
                <FiChevronLeft className="w-4 h-4 text-gray-300" />
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                  let pageNum;
                  if (pagination.total_pages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.current_page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.current_page >= pagination.total_pages - 2) {
                    pageNum = pagination.total_pages - 4 + i;
                  } else {
                    pageNum = pagination.current_page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange?.(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm transition-colors ${
                        pagination.current_page === pageNum
                          ? 'bg-[#ff660e] text-white'
                          : 'bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                {pagination.total_pages > 5 && pagination.current_page < pagination.total_pages - 2 && (
                  <>
                    <span className="px-2 text-gray-500">...</span>
                    <button
                      onClick={() => onPageChange?.(pagination.total_pages)}
                      className={`w-8 h-8 rounded-lg text-sm bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600 transition-colors ${
                        pagination.current_page === pagination.total_pages ? 'bg-[#ff660e] text-white' : ''
                      }`}
                    >
                      {pagination.total_pages}
                    </button>
                  </>
                )}
              </div>
              
              <button
                onClick={() => onPageChange?.(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.total_pages}
                className="p-2 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Next page"
              >
                <FiChevronRight className="w-4 h-4 text-gray-300" />
              </button>
              <button
                onClick={() => onPageChange?.(pagination.total_pages)}
                disabled={pagination.current_page === pagination.total_pages}
                className="p-2 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Last page"
              >
                <FiChevronsRight className="w-4 h-4 text-gray-300" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions Bar (when rows selected) */}
      {selectable && selectedRows.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-700 bg-gray-700/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-[#ff660e]/20 flex items-center justify-center">
                <FiAlertCircle className="text-[#ff660e]" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">{selectedRows.length} items selected</p>
                <p className="text-xs text-gray-400">Choose an action to perform</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <select className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm">
                <option value="">Bulk Actions</option>
                <option value="delete">Delete Selected</option>
                <option value="activate">Activate Selected</option>
                <option value="deactivate">Deactivate Selected</option>
                <option value="export">Export Selected</option>
              </select>
              <button className="px-4 py-2 bg-[#ff660e] text-white rounded hover:bg-orange-600 transition-colors text-sm">
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;